import {
  addDays,
  addHours,
  addMilliseconds,
  addMonths,
  addYears,
  differenceInHours,
  differenceInMilliseconds,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDate,
  getDay,
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  lastDayOfMonth,
  set,
  startOfMonth,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ReoccurrenceEnum } from '@juno/client-api/model';
import { formatTime, safeToDate } from '@juno/utils';

/**
 * ? method getRelativeStartsIn
 * * DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 *
 * @param date_start - The start date of the event
 * @param date_end - The end date of the event
 * @param reoccurrence - The reoccurrence pattern of the event
 * @param time_end - The end time of the event
 * @returns {string} The relative start time of the event
 *
 * ? Note: The function should return the relative start time of the event based on the current date and time such as:
 * ? - "Live" if the event is currently ongoing
 * ? - "Today at {start_time} - {end_time}" if the event is today
 * ? - "Tomorrow at {start_time} - {end_time}" if the event is tomorrow
 * ? - "{day} at {start_time} - {end_time}" if the event is later this week
 * ? - "Ended" if the event has already ended
 *
 ** DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 */

export const getRelativeStartsIn = (
  date_start: string,
  date_end: string,
  reoccurrence?: ReoccurrenceEnum | null,
  time_end?: string | null,
): string => {
  const now = new Date();
  const startDate = new Date(date_start);
  const startTime = new Date(date_start);
  const endTime = time_end ? new Date(time_end) : new Date(date_end);
  const endDate = new Date(date_end);

  // Check if the event has already ended
  if (isBefore(endDate, now)) {
    return 'Ended';
  }

  // Calculate the next occurrence based on reoccurrence pattern
  if (reoccurrence) {
    switch (reoccurrence) {
      case ReoccurrenceEnum.once: {
        // Check if the session is live (including the exact moment it starts)
        if (isBefore(startDate, now) || startDate.getTime() === now.getTime()) {
          if (isAfter(endDate, now)) {
            return 'Live';
          }
        }

        // Check if the event starts and ends today
        if (startDate.toDateString() === now.toDateString()) {
          return `Today at ${format(startTime, 'p')} - ${format(endTime, 'p')}`;
        }

        // Check if the event starts tomorrow
        if (startDate.getDate() === now.getDate() + 1) {
          return `Tomorrow at ${format(startTime, 'p')} - ${format(endTime, 'p')}`;
        }

        // Default: Return the event date and time with the day of the week
        return `${format(startDate, 'EEEE')} at ${format(startTime, 'p')} - ${format(
          endTime,
          'p',
        )}`;
      }

      case ReoccurrenceEnum.daily: {
        if (endDate <= now) {
          return 'Ended';
        }

        const todayStartTime = set(now, {
          hours: getHours(startDate),
          minutes: getMinutes(startDate),
          seconds: getSeconds(startDate),
          milliseconds: getMilliseconds(startDate),
        });

        // Handle case when event is starting and ending in the future
        if (startDate > now) {
          const formattedDay = format(startDate, 'EEEE');
          const formattedStartTime = format(startDate, 'p');
          const formattedEndTime = format(endTime, 'p');
          return `${formattedDay} at ${formattedStartTime} - ${formattedEndTime}`;
        }

        const nowTime = getTotalMilliseconds(now);
        const endTimeTime = getTotalMilliseconds(endTime);

        // Handle case when event is currently ongoing
        if (now >= todayStartTime && nowTime < endTimeTime) {
          return 'Live';
        }

        // Handle case when event already started today but is not live
        if (todayStartTime <= now) {
          // Set the start date to tomorrow
          const nextStartDate = addDays(now, 1);
          const nextStartTime = format(startDate, 'p');
          const nextEndTime = format(endTime, 'p');
          return `${format(nextStartDate, 'EEEE')} at ${nextStartTime} - ${nextEndTime}`;
        }

        const nextStartTime = format(startDate, 'p');
        const nextEndTime = format(endTime, 'p');
        return `Today at ${nextStartTime} - ${nextEndTime}`;
      }

      case ReoccurrenceEnum.weekly: {
        if (endDate > now) {
          const todayStartTime = set(now, {
            hours: getHours(startDate),
            minutes: getMinutes(startDate),
            seconds: getSeconds(startDate),
            milliseconds: getMilliseconds(startDate),
          });

          // Handle case when event is starting and ending in the future
          if (startDate > now && isToday(startDate)) {
            const formattedDay = 'Today';
            const formattedStartTime = format(startDate, 'p');
            const formattedEndTime = format(endTime, 'p');
            return `${formattedDay} at ${formattedStartTime} - ${formattedEndTime}`;
          } else if (startDate > now) {
            const formattedDay = format(startDate, 'EEEE');
            const formattedStartTime = format(startDate, 'p');
            const formattedEndTime = format(endTime, 'p');
            return `${formattedDay} at ${formattedStartTime} - ${formattedEndTime}`;
          }

          // Handle case when event is currently ongoing
          if (now >= todayStartTime && now < endTime) {
            return 'Live';
          }

          const daysUntilNextStart = getNextWeekday(getDay(startDate)); // Calculate days until next occurrence
          let nextStartDate = addDays(now, daysUntilNextStart);
          nextStartDate = set(nextStartDate, {
            hours: getHours(startDate),
            minutes: getMinutes(startDate),
            seconds: getSeconds(startDate),
            milliseconds: getMilliseconds(startDate),
          });

          // Calculate the end date based on the start date and end time
          const nextEndDate = addMilliseconds(
            nextStartDate,
            differenceInMilliseconds(endTime, startDate),
          );

          // Handle case when event starts later this week
          if (nextStartDate > now) {
            const nextStartTime = format(nextStartDate, 'p', { locale: enUS });
            const nextEndTime = format(nextEndDate, 'p', { locale: enUS });
            const formattedDay = format(nextStartDate, 'EEEE', { locale: enUS });
            return `${formattedDay} at ${nextStartTime} - ${nextEndTime}`;
          }

          // Handle case when event starts and ends within the same week
          const expectedStartTime = format(startDate, 'p', { locale: enUS });
          const expectedEndTime = format(endDate, 'p', { locale: enUS });
          const formattedDay = format(nextStartDate, 'EEEE', { locale: enUS });
          return `${formattedDay} at ${expectedStartTime} - ${expectedEndTime}`;
        } else {
          return 'Ended';
        }
      }

      case ReoccurrenceEnum.monthly: {
        // Check if the session is live
        if (isBefore(startDate, now) && isAfter(endTime, now)) {
          return 'Live';
        }

        const startDayOfMonth = startDate.getDate();
        const endDayOfMonth = endTime.getDate();
        const isCrossingMonth = startDayOfMonth !== endDayOfMonth;

        if (isCrossingMonth) {
          // Handle event that crosses from the end of one month to the start of the next
          let nextStartDate = lastDayOfMonth(startDate);

          // Set the correct start time on the next last day of the month
          nextStartDate = set(nextStartDate, {
            hours: startDate.getHours(),
            minutes: startDate.getMinutes(),
            seconds: startDate.getSeconds(),
            milliseconds: startDate.getMilliseconds(),
          });

          // The end time should be on the next day, with the same time as the original end time
          const nextEndDate = addDays(nextStartDate, 1);
          const nextEndTime = set(nextEndDate, {
            hours: endTime.getHours(),
            minutes: endTime.getMinutes(),
            seconds: endTime.getSeconds(),
            milliseconds: endTime.getMilliseconds(),
          });

          // Format the start and end times for the next occurrence
          const formattedStartDay = format(nextStartDate, 'EEEE, MMM d');
          const formattedStartTime = format(nextStartDate, 'p');
          const formattedEndDay = format(nextEndTime, 'EEEE, MMM d');
          const formattedEndTime = format(nextEndTime, 'p');

          return `${formattedStartDay} at ${formattedStartTime} - ${formattedEndDay} at ${formattedEndTime}`;
        }

        // Regular monthly recurrence logic for events that don't cross months
        const startWeekday = getDay(startDate);
        const startWeekIndex = Math.ceil(getDate(startDate) / 7) - 1;

        const occurrencesThisMonth = eachDayOfInterval({
          start: startOfMonth(now),
          end: endOfMonth(now),
        }).filter((day) => getDay(day) === startWeekday);

        let nextOccurrenceStartDate;

        if (startWeekIndex < occurrencesThisMonth.length) {
          nextOccurrenceStartDate = occurrencesThisMonth[startWeekIndex];
        } else {
          nextOccurrenceStartDate = occurrencesThisMonth[occurrencesThisMonth.length - 1];
        }

        if (!nextOccurrenceStartDate || nextOccurrenceStartDate < now) {
          const nextMonth = addMonths(now, 1);
          const occurrencesNextMonth = eachDayOfInterval({
            start: startOfMonth(nextMonth),
            end: endOfMonth(nextMonth),
          }).filter((day) => getDay(day) === startWeekday);

          if (startWeekIndex < occurrencesNextMonth.length) {
            nextOccurrenceStartDate = occurrencesNextMonth[startWeekIndex];
          } else {
            nextOccurrenceStartDate = occurrencesNextMonth[occurrencesNextMonth.length - 1];
          }
        }

        // Special handling for leap year February 29th recurrence
        const isFeb29 = startDate.getMonth() === 1 && startDate.getDate() === 29;
        if (isFeb29) {
          nextOccurrenceStartDate = set(addMonths(startDate, 1), {
            date: 29, // Move to March 29th
            hours: startDate.getHours(),
            minutes: startDate.getMinutes(),
            seconds: startDate.getSeconds(),
            milliseconds: startDate.getMilliseconds(),
          });
        } else {
          nextOccurrenceStartDate = set(nextOccurrenceStartDate as Date, {
            hours: startDate.getHours(),
            minutes: startDate.getMinutes(),
            seconds: startDate.getSeconds(),
            milliseconds: startDate.getMilliseconds(),
          });
        }

        const nextOccurrenceEndDate = addHours(
          nextOccurrenceStartDate,
          differenceInHours(endTime, startDate),
        );

        // Format the date correctly with the proper day of the week
        const formattedDay = format(nextOccurrenceStartDate, 'EEEE'); // Correct day of the week
        const formattedStartTime = format(nextOccurrenceStartDate, 'p');
        const formattedEndTime = format(nextOccurrenceEndDate, 'p');
        const formattedStartDate = format(nextOccurrenceStartDate, 'MMM d');

        return `${formattedDay}, ${formattedStartDate} at ${formattedStartTime} - ${formattedEndTime}`;
      }

      case ReoccurrenceEnum.yearly: {
        // Parse the string input into Date objects
        const parsedStartDate = new Date(date_start);
        const parsedEndTime = new Date(time_end || date_end);
        const now = new Date();

        // Direct comparison to check if the event is live
        if (parsedStartDate.getTime() < now.getTime() && parsedEndTime.getTime() > now.getTime()) {
          return 'Live';
        }

        // Clone the parsed dates for next occurrence
        const nextOccurrenceStartDate = new Date(parsedStartDate);
        const nextOccurrenceEndTime = new Date(parsedEndTime);

        // Ensure the next occurrence is in the future: if in the past, add 1 year until it's in the future
        while (nextOccurrenceStartDate.getTime() < now.getTime()) {
          nextOccurrenceStartDate.setFullYear(nextOccurrenceStartDate.getFullYear() + 1);
          nextOccurrenceEndTime.setFullYear(nextOccurrenceEndTime.getFullYear() + 1);
        }

        // Format the start and end times for the next occurrence
        const formattedDay = format(nextOccurrenceStartDate, 'EEEE'); // Day of the week
        const formattedStartTime = format(nextOccurrenceStartDate, 'p'); // Start time in AM/PM
        const formattedEndTime = format(nextOccurrenceEndTime, 'p'); // End time in AM/PM
        const formattedStartDate = format(nextOccurrenceStartDate, 'MMM d'); // Exact date

        return `${formattedDay}, ${formattedStartDate} at ${formattedStartTime} - ${formattedEndTime}`;
      }
    }
  } else {
    // Handle non-recurring sessions
    if (startDate < now) {
      return 'Ended';
    }
  }

  // Default formatting for one-time events
  const formattedDay = format(startDate, 'EEEE');
  const formattedStartTime = format(startDate, 'p');
  const formattedEndTime = format(endTime, 'p');

  return `${formattedDay} at ${formattedStartTime} - ${formattedEndTime}`;
};

const getNextWeekday = (dayOfWeek: number) => {
  const today = new Date().getDay();
  const daysUntilNext = (dayOfWeek + (7 - today)) % 7;
  return daysUntilNext === 0 ? 7 : daysUntilNext;
};

const getTotalMilliseconds = (date: Date) => {
  return (
    getHours(date) * 60 * 60 * 1000 +
    getMinutes(date) * 60 * 1000 +
    getSeconds(date) * 1000 +
    getMilliseconds(date)
  );
};

/**
 * Format Start Date into "Thursday May 1, 2025"
 */
export const formatDisplayDate = (dateStr: string) => {
  const date = safeToDate(dateStr);
  return date
    ? date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
};

/**
 * Format Time into "11:23 AM"
 */
export const formatDisplayTimeOnly = (dateStr: string) => {
  const date = safeToDate(dateStr);
  return date ? formatTime(date) : '';
};

/**
 * ? method calculateDuration
 * * DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 *
 * Calculates the duration between a start and end timestamp.
 *
 * @param startStr - ISO string of the session start time
 * @param endStr - ISO string of the session end time
 * @returns {string} A human-readable duration (e.g., "1 hour", "1 hour 15 mins", "45 mins")
 *
 * * DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 */
export const calculateDuration = (startStr: string, endStr: string): string => {
  const start = new Date(startStr);
  const end = new Date(endStr);

  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const diffMinutes = endMinutes - startMinutes;

  if (diffMinutes <= 0) {
    return '0 mins'; // could return '' if we want to show nothing
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);

  return parts.join(' ');
};

/**
 * ? method calculateNextOccurrence
 * * DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 * Calculates the next occurrence of a session based on the start date, recurrence pattern, and (optional) series end date.
 *
 * @param startStr - ISO string of the session start time (e.g., "2024-05-28T20:30:00Z")
 * @param recurrence - Recurrence pattern (e.g., daily, weekly, monthly, yearly, or once)
 * @param seriesEndStr - (Optional) ISO string of when the overall recurring series ends
 * @returns {string} A human-readable label for the next occurrence (e.g., "Today at 8:30 PM", "Tomorrow at 8:30 PM", "Ended")
 *
 * * DO NOT EDIT THIS METHOD WITHOUT A TEST CASE
 */

export const calculateNextOccurrence = (
  startStr: string,
  recurrence: ReoccurrenceEnum | null,
  seriesEndStr?: string | null,
): string => {
  const now = new Date();
  const startDate = new Date(startStr);
  const seriesEndDate = seriesEndStr ? new Date(seriesEndStr) : null;

  if (seriesEndDate && now > seriesEndDate) {
    return 'Ended';
  }

  let nextOccurrence = startDate;

  if (recurrence === ReoccurrenceEnum.once || !recurrence) {
    if (now < startDate) {
      nextOccurrence = startDate;
    } else if (now >= startDate && (!seriesEndDate || now <= seriesEndDate)) {
      return 'Ongoing';
    } else {
      return 'Ended';
    }
  } else {
    switch (recurrence) {
      case ReoccurrenceEnum.daily:
        nextOccurrence = sameTimeTodayOrTomorrow(startDate, now);
        break;

      case ReoccurrenceEnum.weekly:
        nextOccurrence = nextWeeklyOccurrence(startDate, now);
        break;

      case ReoccurrenceEnum.monthly:
        nextOccurrence = nextMonthlyOccurrence(startDate, now);
        break;

      case ReoccurrenceEnum.yearly:
        nextOccurrence = nextYearlyOccurrence(startDate, now);
        break;
    }

    if (seriesEndDate && nextOccurrence > seriesEndDate) {
      return 'Ended';
    }
  }

  return formatNextOccurrenceLabel(nextOccurrence, now);
};

const sameTimeTodayOrTomorrow = (startDate: Date, now: Date) => {
  const todayWithStartTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
    startDate.getMilliseconds(),
  );

  return now < todayWithStartTime ? todayWithStartTime : addDays(todayWithStartTime, 1);
};

const nextWeeklyOccurrence = (startDate: Date, now: Date) => {
  const targetWeekday = startDate.getDay();
  const todayWeekday = now.getDay();
  const daysUntilNext = (targetWeekday - todayWeekday + 7) % 7;

  const todayWithStartTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
    startDate.getMilliseconds(),
  );

  if (daysUntilNext === 0 && now < todayWithStartTime) {
    return todayWithStartTime;
  }

  const nextDay = addDays(todayWithStartTime, daysUntilNext || 7);
  return nextDay;
};

const nextMonthlyOccurrence = (startDate: Date, now: Date) => {
  let next = new Date(
    now.getFullYear(),
    now.getMonth(),
    startDate.getDate(),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
    startDate.getMilliseconds(),
  );

  if (isBefore(next, now)) {
    next = addMonths(next, 1);
  }

  return next;
};

const nextYearlyOccurrence = (startDate: Date, now: Date) => {
  let next = new Date(
    now.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
    startDate.getMilliseconds(),
  );

  if (isBefore(next, now)) {
    next = addYears(next, 1);
  }

  return next;
};

const formatNextOccurrenceLabel = (occurrence: Date, now: Date) => {
  if (isToday(occurrence)) {
    return `Today at ${format(occurrence, 'p')}`;
  }
  if (isTomorrow(occurrence)) {
    return `Tomorrow at ${format(occurrence, 'p')}`;
  }

  const diffInDays = Math.floor((occurrence.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays <= 6 && occurrence.getFullYear() === now.getFullYear()) {
    // Within the same week
    return `${format(occurrence, 'EEEE')} at ${format(occurrence, 'p')}`;
  }

  if (occurrence.getFullYear() === now.getFullYear()) {
    // Same year but beyond a week — show Month and Day
    return `${format(occurrence, 'EEEE')}, ${format(occurrence, 'MMM d')} at ${format(
      occurrence,
      'p',
    )}`;
  }

  // Different year
  return `${format(occurrence, 'EEEE')}, ${format(occurrence, 'MMM d, yyyy')} at ${format(
    occurrence,
    'p',
  )}`;
};
